/**
 * Componente: HistorialCumplimientosDialog
 *
 * Modal para mostrar el historial de cumplimientos de una programación.
 *
 * Legacy: ListarCumplimientosViewImpl.java -> popup "Lista de Cumplimientos"
 *
 * Columnas del modal:
 * 1. Fecha Cumplimiento (feprogramacion)
 * 2. Cantidad Ejecutada (nmcantidadEjecutada)
 * 3. Núm. Asistentes (nmasistentes)
 * 4. Nombre Contacto (dsnombreContacto)
 * 5. Email Contacto (dsemailContacto)
 * 6. Transporte Empleado (ptvalorTransporte)
 * 7. Transporte Adicional (ptvalorTransporteAdicional)
 * 8. Estado Acta (calculado con validacionActa())
 *
 * Acciones:
 * - Estado (info): Muestra alert con estado del acta
 * - Asociar Acta: Abre URL externa G360
 * - Acta 360 (PDF): Descarga PDF del acta
 * - Observaciones/Recomendaciones: Abre modal de observaciones
 *
 * @author Emerson Rodriguez - Ingeneo
 */
import {
  Component,
  input,
  output,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

import { LoadingOverlayComponent } from 'src/app/presentation/shared/loading-overlay.component';
import {
  HistorialCumplimiento,
  TIPO_CITA_CAMPO
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import { openExternalWindow } from 'src/app/common/window-utils';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { historialCumplimientosDialogPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Estados de acta posibles.
 * Legacy: ListarCumplimientosViewImpl.validacionActa()
 */
type EstadoActa = 'N/A' | 'SIN ASOCIAR ACTA' | 'ACTA ASOCIADA' | string;

/**
 * Lista de contratos que no aplican para acta.
 * Legacy: ConstantsEJB.CONTRATOS_CUMPL (parámetro general)
 * TODO: Obtener del backend en lugar de hardcodear
 */
const CONTRATOS_CUMPL: string[] = [];

@Component({
  selector: 'app-historial-cumplimientos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    TooltipModule,
    LoadingOverlayComponent,
    BusquedaPaginadorComponent,
    DatePipe,
    DecimalPipe
  ],
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Lista de Cumplimientos"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '1400px' }"
      [closable]="true"
      (onHide)="onCerrar()"
    >
      <app-loading-overlay [blocked]="loading()" />

      <!-- Banner post-asociación exitosa -->
      @if (actaRecienAsociada()) {
        <div
          class="flex items-center gap-3 mb-3 p-3 rounded border border-green-300 bg-green-50 text-green-800 text-sm"
        >
          <i class="pi pi-check-circle text-lg"></i>
          <span class="flex-1"
            >El acta fue generada exitosamente. El estado puede tardar unos
            segundos en actualizarse.</span
          >
          <button
            pButton
            label="Recargar"
            icon="pi pi-refresh"
            class="p-button-sm p-button-outlined p-button-success min-w-[44px] min-h-[44px]"
            (click)="onRecargarHistorial()"
          ></button>
        </div>
      }

      <!-- Header con info de paginación -->
      <div class="flex justify-between items-center mb-3">
        <span class="text-sm text-gray-600">
          @if (totalRegistros() > 0) {
            Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
            {{ totalRegistros() }}
          } @else {
            No hay cumplimientos registrados
          }
        </span>
      </div>

      <!-- Tabla -->
      <div class="md:hidden space-y-3 p-3">
        @if (paginatedData().length === 0) {
          <div class="text-center py-4 text-gray-500">
            No hay cumplimientos registrados para esta programación
          </div>
        }
        @for (cumplimiento of paginatedData(); track $index) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="mb-3 min-w-0">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Fecha cumplimiento
              </div>
              <div class="font-semibold text-gray-800 dark:text-gray-100">
                {{
                  textoTabla(cumplimiento.feprogramacion | date: 'dd/MM/yyyy')
                }}
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm mb-3">
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Cant. ejecutada:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(cumplimiento.nmcantidadEjecutada | number: '1.0-2')
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Núm. asistentes:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(cumplimiento.nmasistentes)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Nombre contacto:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(cumplimiento.dsnombreContacto)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Email contacto:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(cumplimiento.dsemailContacto)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Transp. empleado:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(cumplimiento.ptvalorTransporte | number: '1.0-2')
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Transp. adicional:</span
                >
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(
                    cumplimiento.ptvalorTransporteAdicional | number: '1.0-2'
                  )
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >Estado acta:</span
                >
                <span class="ml-1" [class]="getEstadoActaClass(cumplimiento)">{{
                  textoTabla(getEstadoActa(cumplimiento))
                }}</span>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <button
                class="min-w-[44px] min-h-[44px] p-button-outlined p-button-sm"
                pButton
                icon="pi pi-info-circle"
                pTooltip="Ver estado del acta"
                (click)="onVerEstado(cumplimiento)"
              ></button>
              @if (puedeAsociarActa(cumplimiento)) {
                <button
                  class="min-w-[44px] min-h-[44px] p-button-outlined p-button-success p-button-sm"
                  pButton
                  icon="pi pi-external-link"
                  pTooltip="Asociar Acta"
                  [disabled]="!cumplimiento.dsemailContacto"
                  (click)="onAsociarActa(cumplimiento)"
                ></button>
              } @else {
                <span></span>
              }
              @if (cumplimiento.cdconsecutivoActa) {
                <button
                  class="min-w-[44px] min-h-[44px] p-button-outlined p-button-danger p-button-sm"
                  pButton
                  icon="pi pi-file-pdf"
                  pTooltip="Descargar Acta 360"
                  (click)="onDescargarActa(cumplimiento)"
                ></button>
              } @else {
                <span></span>
              }
            </div>
            <button
              pButton
              icon="pi pi-search"
              label="Observaciones / Recomendaciones"
              class="p-button-text p-button-sm min-h-[44px] w-full mt-2"
              (click)="onVerObservaciones(cumplimiento)"
            ></button>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="historialTableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="paginatedData()"
          [paginator]="false"
          [rows]="pageSize()"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [scrollHeight]="
            historialTableConfig().responsive.scrollHeight || '360px'
          "
          [styleClass]="historialTableStyleClass()"
        >
          <!-- Legacy: ListarCumplimientosViewImpl.java - crearSuraTableCumplimientos() -->
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn class="text-center" style="width: 110px">
                <span class="grid-header-text" title="Fecha Cumplimiento"
                  >Fecha Cumplimiento</span
                >
              </th>
              <th pResizableColumn class="text-center" style="width: 80px">
                <span class="grid-header-text" title="Cantidad Ejecutada"
                  >Cant. Ejecutada</span
                >
              </th>
              <th pResizableColumn class="text-center" style="width: 80px">
                <span class="grid-header-text" title="Número Asistentes"
                  >Núm. Asistentes</span
                >
              </th>
              <th pResizableColumn style="min-width: 140px">
                <span class="grid-header-text" title="Nombre Contacto"
                  >Nombre Contacto</span
                >
              </th>
              <th pResizableColumn style="min-width: 160px">
                <span class="grid-header-text" title="Email Contacto"
                  >Email Contacto</span
                >
              </th>
              <th pResizableColumn class="text-right" style="width: 90px">
                <span class="grid-header-text" title="Transporte Empleado"
                  >Transp. Emp.</span
                >
              </th>
              <th pResizableColumn class="text-right" style="width: 90px">
                <span class="grid-header-text" title="Transporte Adicional"
                  >Transp. Adic.</span
                >
              </th>
              <th pResizableColumn class="text-center" style="width: 110px">
                <span class="grid-header-text" title="Estado Acta"
                  >Estado Acta</span
                >
              </th>
              <!-- Legacy: 3 columnas de acciones separadas -->
              <th pResizableColumn class="text-center" style="width: 90px">
                <span class="grid-header-text" title="Asociar Acta"
                  >Asociar Acta</span
                >
              </th>
              <th pResizableColumn class="text-center" style="width: 65px">
                <span class="grid-header-text" title="Acta 360">Acta 360</span>
              </th>
              <th pResizableColumn class="text-center" style="width: 65px">
                <span
                  class="grid-header-text"
                  title="Observaciones/Recomendaciones"
                  >Obs/Rec</span
                >
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-cumplimiento>
            <tr>
              <td class="text-center">
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(
                      cumplimiento.feprogramacion | date: 'dd/MM/yyyy'
                    )
                  "
                  >{{
                    textoTabla(cumplimiento.feprogramacion | date: 'dd/MM/yyyy')
                  }}</span
                >
              </td>
              <td class="text-center">
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(
                      cumplimiento.nmcantidadEjecutada | number: '1.0-2'
                    )
                  "
                  >{{
                    textoTabla(
                      cumplimiento.nmcantidadEjecutada | number: '1.0-2'
                    )
                  }}</span
                >
              </td>
              <td class="text-center">
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(cumplimiento.nmasistentes)"
                  >{{ textoTabla(cumplimiento.nmasistentes) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(cumplimiento.dsnombreContacto)"
                  >{{ textoTabla(cumplimiento.dsnombreContacto) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(cumplimiento.dsemailContacto)"
                  >{{ textoTabla(cumplimiento.dsemailContacto) }}</span
                >
              </td>
              <td class="text-right">
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(
                      cumplimiento.ptvalorTransporte | number: '1.0-2'
                    )
                  "
                  >{{
                    textoTabla(cumplimiento.ptvalorTransporte | number: '1.0-2')
                  }}</span
                >
              </td>
              <td class="text-right">
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(
                      cumplimiento.ptvalorTransporteAdicional | number: '1.0-2'
                    )
                  "
                  >{{
                    textoTabla(
                      cumplimiento.ptvalorTransporteAdicional | number: '1.0-2'
                    )
                  }}</span
                >
              </td>
              <td class="text-center">
                <span
                  class="grid-cell-text"
                  [class]="getEstadoActaClass(cumplimiento)"
                  [attr.title]="tituloTabla(getEstadoActa(cumplimiento))"
                >
                  {{ textoTabla(getEstadoActa(cumplimiento)) }}
                </span>
              </td>

              <!-- Columna Asociar Acta - Legacy: info.png + EnviarSAP.png -->
              <td class="text-center">
                <div class="flex gap-1 justify-center">
                  <!-- Info estado - Legacy: images/information.png -->
                  <button
                    pButton
                    icon="pi pi-info-circle"
                    class="p-button-rounded p-button-text p-button-sm p-button-info min-w-[44px] min-h-[44px]"
                    pTooltip="Ver estado del acta"
                    tooltipPosition="top"
                    (click)="onVerEstado(cumplimiento)"
                  ></button>
                  <!-- Link asociar - Legacy: images/EnviarSAP.png (solo si aplica) -->
                  @if (puedeAsociarActa(cumplimiento)) {
                    <button
                      pButton
                      icon="pi pi-external-link"
                      class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                      pTooltip="Asociar Acta"
                      tooltipPosition="top"
                      [disabled]="!cumplimiento.dsemailContacto"
                      (click)="onAsociarActa(cumplimiento)"
                    ></button>
                  }
                </div>
              </td>

              <!-- Columna Acta 360 (PDF) - Legacy: images/pdf.gif - Solo si tiene acta -->
              <td class="text-center">
                @if (cumplimiento.cdconsecutivoActa) {
                  <button
                    pButton
                    icon="pi pi-file-pdf"
                    class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                    pTooltip="Descargar Acta 360"
                    tooltipPosition="top"
                    (click)="onDescargarActa(cumplimiento)"
                  ></button>
                }
              </td>

              <!-- Columna Observaciones/Recomendaciones - Legacy: images/lookup.gif -->
              <td class="text-center">
                <button
                  pButton
                  icon="pi pi-search"
                  class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Observaciones / Recomendaciones"
                  tooltipPosition="top"
                  (click)="onVerObservaciones(cumplimiento)"
                ></button>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="11" class="text-center py-4 text-gray-500">
                No hay cumplimientos registrados para esta programación
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Footer de Paginación -->
      @if (totalRegistros() > 0) {
        <app-busqueda-paginador
          [currentPage]="currentPage()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
          (first)="irPrimeraPagina()"
          (previous)="irPaginaAnterior()"
          (next)="irPaginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      <!-- Footer del Dialog -->
      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <button
            pButton
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined min-h-[44px]"
            (click)="onCerrar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class HistorialCumplimientosDialogComponent {
  // === Inputs/Outputs ===

  /** Si el dialog está visible */
  visible = input<boolean>(false);

  /** Lista de cumplimientos a mostrar */
  cumplimientos = input<HistorialCumplimiento[]>([]);

  /** Lista de contratos que no aplican para acta (CONTRATOS_CUMPL) */
  contratosCumpl = input<string[]>([]);

  /** Evento al cerrar el dialog */
  cerrar = output<void>();

  /** Evento al ver observaciones de un cumplimiento */
  verObservaciones = output<HistorialCumplimiento>();

  /** Evento para asociar acta usando formulario interno (reemplaza URL externa G360) */
  asociarActa = output<HistorialCumplimiento>();

  /** Evento para recargar la lista después de asociar acta - Legacy: stCumplimientos.clear() */
  recargarHistorial = output<void>();

  /** Indica que se acaba de asociar un acta exitosamente (muestra banner de recarga) */
  actaRecienAsociada = input<boolean>(false);

  // === Services ===

  private readonly messageService = inject(MessageService);
  private readonly cumplimientoRepo = inject(CumplimientoRepository);

  // === State ===

  dialogVisible = false;
  loading = signal(false);

  // === Paginación (client-side según skill) ===

  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly historialTableConfig = computed(() =>
    historialCumplimientosDialogPreset.createConfig({
      data: this.paginatedData() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-historial-cumplimientos',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      pagination: {
        enabled: false,
        mode: 'client',
        pageSize: this.pageSize(),
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No hay cumplimientos registrados para esta programación'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '92rem',
        scrollHeight: '360px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de historial de cumplimientos',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly historialTableStyleClass = computed(() => {
    const config = this.historialTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // === Computed ===

  readonly allData = computed(() => this.cumplimientos());
  readonly totalRegistros = computed(() => this.allData().length);
  readonly totalPages = computed(
    () => Math.ceil(this.totalRegistros() / this.pageSize()) || 1
  );

  readonly paginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.allData().slice(start, start + this.pageSize());
  });

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    return Math.min(
      this.currentPage() * this.pageSize(),
      this.totalRegistros()
    );
  });

  readonly hasPrevious = computed(() => this.currentPage() > 1);
  readonly hasNext = computed(() => this.currentPage() < this.totalPages());

  // === Lifecycle ===

  ngOnChanges(): void {
    this.dialogVisible = this.visible();

    // Reset paginación cuando cambia la lista
    if (this.visible()) {
      this.currentPage.set(1);
    }
  }

  // === Métodos de Paginación ===

  irPrimeraPagina(): void {
    this.currentPage.set(1);
  }

  irUltimaPagina(): void {
    this.currentPage.set(this.totalPages());
  }

  irPaginaAnterior(): void {
    if (this.hasPrevious()) {
      this.currentPage.update((p) => p - 1);
    }
  }

  irPaginaSiguiente(): void {
    if (this.hasNext()) {
      this.currentPage.update((p) => p + 1);
    }
  }

  onPageSizeChange(): void {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  // === Lógica validacionActa() - Legacy: ListarCumplimientosViewImpl.java#L512-538 ===

  /**
   * Calcula el estado del acta para mostrar en la tabla.
   *
   * Legacy: validacionActa() en ListarCumplimientosViewImpl.java
   *
   * Reglas:
   * 1. Si tipoCita != CAMPO (17) -> "N/A"
   * 2. Si contrato está en CONTRATOS_CUMPL -> "N/A"
   * 3. Si cdconsecutivoActa == null -> "SIN ASOCIAR ACTA"
   * 4. Si tiene acta -> dsestadoActa de TGFC_CUMPLIM_ACTA
   */
  getEstadoActa(cumplimiento: HistorialCumplimiento): EstadoActa {
    // 1. Si no es tipo CAMPO, no aplica
    if (cumplimiento.nmconsecutivoTipoCita !== TIPO_CITA_CAMPO) {
      return 'N/A';
    }

    // 2. Si contrato está en CONTRATOS_CUMPL, no aplica
    const contratosCumplActual = this.contratosCumpl();
    if (cumplimiento.cdcontrato && contratosCumplActual.length > 0) {
      const contratoNormalizado = cumplimiento.cdcontrato.trim().toLowerCase();
      if (
        contratosCumplActual.some(
          (c) => c.trim().toLowerCase() === contratoNormalizado
        )
      ) {
        return 'N/A';
      }
    }

    // 3. Si no tiene acta asociada
    if (!cumplimiento.cdconsecutivoActa) {
      return 'SIN ASOCIAR ACTA';
    }

    // 4. Si tiene acta, retorna el estado del acta
    return cumplimiento.dsestadoActa || 'ACTA ASOCIADA';
  }

  /**
   * Obtiene la clase CSS para el badge del estado del acta.
   */
  getEstadoActaClass(cumplimiento: HistorialCumplimiento): string {
    const estado = this.getEstadoActa(cumplimiento);

    switch (estado) {
      case 'N/A':
        return 'text-gray-500 text-xs';
      case 'SIN ASOCIAR ACTA':
        return 'text-orange-600 font-semibold text-xs';
      case 'ACTA ASOCIADA':
        return 'text-green-600 font-semibold text-xs';
      case 'EXITOSO':
        return 'text-green-600 font-semibold text-xs';
      case 'ERROR':
      case 'RECIBIDO 360':
        return 'text-red-600 font-semibold text-xs';
      default:
        return 'text-blue-600 font-semibold text-xs';
    }
  }

  /**
   * Determina si se puede mostrar el botón de asociar acta.
   * Legacy: linkColumn en ListarCumplimientosViewImpl.java líneas 325-370
   *
   * Se muestra si estado != 'ACTA ASOCIADA' y != 'N/A' y != 'RECIBIDO 360'.
   */
  puedeAsociarActa(cumplimiento: HistorialCumplimiento): boolean {
    const estado = this.getEstadoActa(cumplimiento);
    return (
      estado !== 'ACTA ASOCIADA' &&
      estado !== 'N/A' &&
      estado !== 'RECIBIDO 360'
    );
  }

  // === Acciones del Modal ===

  /**
   * Muestra alert con el estado del acta.
   * Legacy: visualizarEstadoActa() en ListarCumplimientosViewImpl.java#L540-556
   */
  onVerEstado(cumplimiento: HistorialCumplimiento): void {
    const estado = this.getEstadoActa(cumplimiento);
    let message = '';
    let severity: 'info' | 'warn' | 'success' | 'error' = 'info';

    switch (estado) {
      case 'N/A':
        message = 'El cumplimiento no aplica para asociar Acta';
        severity = 'info';
        break;
      case 'SIN ASOCIAR ACTA':
        message =
          'El acta no se encuentra asociada, debe de realizar el procedimiento de asociar el acta al cumplimiento.';
        severity = 'warn';
        break;
      case 'ACTA ASOCIADA':
        message =
          'El acta ya se encuentra asociada al cumplimiento, esta en espera de resultado de procesamiento.';
        severity = 'success';
        break;
      case 'EXITOSO':
        message = 'El acta fue procesada exitosamente.';
        severity = 'success';
        break;
      case 'ERROR':
      case 'RECIBIDO 360':
      default:
        // Legacy: "Ocurre un error en el proceso de asociacion, por favor validar con la oficina."
        message =
          'Ocurre un error en el proceso de asociación, por favor validar con la oficina.';
        severity = 'error';
        break;
    }

    this.messageService.add({
      severity,
      summary: 'Estado del Acta',
      detail: message,
      life: 5000
    });
  }

  /**
   * Emite evento para asociar acta usando formulario interno (modo soloActa).
   * Reemplaza la apertura de URL externa G360.
   *
   * Validaciones previas:
   * 1. Si no tiene email -> error
   * 2. Si estado es ACTA ASOCIADA/EXITOSO/RECIBIDO 360 -> no permite
   * 3. Solo permite si estado es vacío o ERROR
   */
  onAsociarActa(cumplimiento: HistorialCumplimiento): void {
    // 1. Validar email - Legacy: "Debe tener un correo electronico para asociar el acta"
    if (!cumplimiento.dsemailContacto) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe tener un correo electrónico para asociar el acta',
        life: 4000
      });
      return;
    }

    const estado = this.getEstadoActa(cumplimiento);

    // No se permite si ya tiene acta asociada/exitoso/recibido 360
    if (
      estado === 'ACTA ASOCIADA' ||
      estado === 'EXITOSO' ||
      estado === 'RECIBIDO 360'
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'El acta ya se encuentra asociada al cumplimiento.',
        life: 4000
      });
      return;
    }

    // Emitir evento para que el facade abra el formulario en modo soloActa
    this.asociarActa.emit(cumplimiento);
  }

  /**
   * Descarga PDF del acta desde G360.
   * Legacy: descargarActa360() en ListarCumplimientosActivity.java#L282-349
   *
   * 1. Busca el acta con COMMAND_SEARCH_ACTA_GIROP (fresh query)
   * 2. Si hay acta -> descarga PDF
   * 3. Si NO hay acta -> mensaje "No se encontró el cumplimiento asociado a un acta en gestión 360."
   */
  async onDescargarActa(cumplimiento: HistorialCumplimiento): Promise<void> {
    this.loading.set(true);

    try {
      // Legacy: Siempre hace query fresca a TGFC_CUMPLIM_ACTA (no usa valor cacheado)
      const acta = await this.cumplimientoRepo.buscarActaCumplimiento(
        cumplimiento.nmconsecutivo
      );

      if (acta && acta.cdconsecutivoActa) {
        // Legacy: consumirServicio(dto) -> abre ventana con URL de descarga
        const url = this.cumplimientoRepo.getDescargarActaUrl(
          cumplimiento.nmconsecutivo
        );
        openExternalWindow(
          url,
          'toolbar=yes,resizable=yes,left=0,top=0,menubar=yes,location=yes,status=yes,scrollbars=yes'
        );
      } else {
        // Legacy: cons.busquedaActaGirop() = "No se encontró el cumplimiento asociado a un acta en gestión 360."
        this.messageService.add({
          severity: 'warn',
          summary: 'Acta no encontrada',
          detail:
            'No se encontró el cumplimiento asociado a un acta en gestión 360.',
          life: 4000
        });
      }
    } catch {
      // Legacy: cons.genericError()
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al buscar el acta del cumplimiento.',
        life: 4000
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Emite evento para abrir modal de observaciones/recomendaciones.
   */
  onVerObservaciones(cumplimiento: HistorialCumplimiento): void {
    this.verObservaciones.emit(cumplimiento);
  }

  /**
   * Recarga el historial (emit al padre para refrescar datos del backend).
   */
  onRecargarHistorial(): void {
    this.recargarHistorial.emit();
  }

  /**
   * Cierra el dialog.
   */
  onCerrar(): void {
    this.dialogVisible = false;
    this.cerrar.emit();
  }
}
